import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Play, ChevronRight, Tag } from "lucide-react";
import BRAND from "../../brand";
import { CATEGORIES, CATEGORY_COLORS } from "../../constants/scenarioCategories";

const getDifficultyColor = (difficulty) => {
  switch (difficulty) {
    case "easy": return "bg-[#14B8A6]/10 text-[#14B8A6]";
    case "medium": return "bg-[#F59E0B]/10 text-[#F59E0B]";
    case "hard": return "bg-red-50 text-red-600";
    default: return "bg-slate-100 text-slate-600";
  }
};

export function ScenarioList({ scenarios }) {
  const [activeCategory, setActiveCategory] = useState("all");
  const usedCategories = [...new Set(scenarios.map((s) => s.category || "other"))];
  const filtered = activeCategory === "all" ? scenarios : scenarios.filter((s) => (s.category || "other") === activeCategory);

  return (
    <Card className="premium-card fade-up">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl font-bold text-[#0B1F3B]">{BRAND.terms.scenarios}</CardTitle>
            <CardDescription>Choose a scenario to start practicing</CardDescription>
          </div>
          <Link to="/simulator">
            <Button className="bg-[#0B1F3B] hover:bg-[#0B1F3B]/90 btn-premium" data-testid="start-practice-btn">
              <Play className="w-4 h-4 mr-2" /> Start Practice
            </Button>
          </Link>
        </div>
        {/* Category filter pills */}
        <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="dashboard-category-filter">
          <button onClick={() => setActiveCategory("all")}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${activeCategory === "all" ? "bg-[#0B1F3B] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
            All ({scenarios.length})
          </button>
          {usedCategories.map((catId) => (
            <button key={catId} onClick={() => setActiveCategory(catId)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${activeCategory === catId ? "bg-[#0B1F3B] text-white" : `${CATEGORY_COLORS[catId] || "bg-slate-100 text-slate-500"} hover:opacity-80`}`}>
              {CATEGORIES[catId] || catId} ({scenarios.filter((s) => (s.category || "other") === catId).length})
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {filtered.map((scenario) => (
            <Link
              key={scenario.id}
              to={`/simulator?scenario=${scenario.id}`}
              className="block"
              data-testid={`scenario-${scenario.id}`}
            >
              <div className="p-4 rounded-xl border border-slate-200/60 hover:border-[#2563EB]/30 hover:bg-[#2563EB]/5 transition-all group">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-semibold text-[#0B1F3B] group-hover:text-[#2563EB]">{scenario.name}</h3>
                      <Badge variant="outline" className={getDifficultyColor(scenario.difficulty)}>
                        {scenario.difficulty}
                      </Badge>
                      <Badge variant="outline" className={`${CATEGORY_COLORS[scenario.category || "other"]} text-[10px]`}>
                        <Tag className="w-3 h-3 mr-0.5" />{CATEGORIES[scenario.category || "other"]}
                      </Badge>
                      {scenario.is_custom && <Badge variant="outline" className="bg-[#2563EB]/5 text-[#2563EB] text-[10px]">Custom</Badge>}
                    </div>
                    <p className="text-sm text-slate-500 line-clamp-2">{scenario.description}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-[#2563EB] transition-colors flex-shrink-0 mt-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
